import { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';
import AddProductModal from './AddProductModal';

const EDGE_FN_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth';
const getAdminToken = () => localStorage.getItem('admin_token') ?? '';

async function productAPI(action: string, params: Record<string, unknown>) {
  const res = await fetch(EDGE_FN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, token: getAdminToken(), ...params }),
  });
  return res.json();
}

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  original_price: number | null;
  images: string[] | null;
  stock: number;
  sales: number;
  status: string;
  created_at: string;
  description: string | null;
  sku: string | null;
  weight: number | null;
  points_per_box: number | null;
  boxes_per_order: number | null;
  sort_order: number | null;
  shipping_type: string | null;
  shipping_template: string | null;
  stock_deduct: string | null;
  purchase_limit: number | null;
}

const statusStyle: Record<string, string> = {
  '上架': 'bg-emerald-50 text-emerald-600',
  '下架': 'bg-gray-100 text-gray-500',
  '售罄': 'bg-rose-50 text-rose-500',
};

// 行内快速编辑价格/库存
const InlineEdit = ({
  value,
  onSave,
  prefix = '',
  suffix = '',
}: {
  value: number;
  onSave: (v: number) => Promise<void>;
  prefix?: string;
  suffix?: string;
}) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(value));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = () => { setVal(String(value)); setEditing(true); setTimeout(() => inputRef.current?.select(), 50); };
  const cancel = () => { setEditing(false); setVal(String(value)); };
  const save = async () => {
    const num = Number(val);
    if (isNaN(num) || num < 0) { cancel(); return; }
    setSaving(true);
    await onSave(num);
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
          className="w-24 border border-rose-400 rounded px-2 py-1 text-sm outline-none"
          disabled={saving}
        />
        <button onClick={save} disabled={saving} className="w-6 h-6 flex items-center justify-center bg-emerald-500 rounded text-white cursor-pointer hover:bg-emerald-600 disabled:opacity-50">
          {saving ? <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-check-line text-xs" />}
        </button>
        <button onClick={cancel} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-gray-500 cursor-pointer hover:bg-gray-200">
          <i className="ri-close-line text-xs" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={open}
      className="group flex items-center gap-1 cursor-pointer hover:text-rose-500 transition-colors"
      title="点击快速编辑"
    >
      <span className="text-sm font-semibold text-gray-800 group-hover:text-rose-500">
        {prefix}{Number(value).toFixed(2)}{suffix}
      </span>
      <i className="ri-edit-line text-xs text-gray-300 group-hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

const AdminProductsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('全部');
  const [updating, setUpdating] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setProducts(data as Product[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const quickUpdate = async (id: string, field: 'price' | 'original_price' | 'stock' | 'sales', value: number) => {
    const result = await productAPI('product_update', { id, payload: { [field]: value } });
    if (!result.error) {
      setProducts((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));
    }
  };

  const toggleStatus = async (product: Product) => {
    const newStatus = product.status === '上架' ? '下架' : '上架';
    setUpdating(product.id);
    const result = await productAPI('product_update', { id: product.id, payload: { status: newStatus } });
    if (!result.error) {
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, status: newStatus } : p));
    }
    setUpdating(null);
  };

  const handleDelete = async () => {
    if (!deletingProduct) return;
    setDeleting(true);
    const result = await productAPI('product_delete', { id: deletingProduct.id });
    if (!result.error) {
      setProducts((prev) => prev.filter((p) => p.id !== deletingProduct.id));
    }
    setDeleting(false);
    setDeletingProduct(null);
  };

  const filtered = products.filter((p) => {
    const matchSearch = p.name.includes(search) || (p.category ?? '').includes(search);
    const matchStatus = filterStatus === '全部' || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <AdminLayout title="商品管理">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <i className="ri-search-line text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索商品名称/分类"
              className="text-sm outline-none w-52 placeholder-gray-400"
            />
          </div>
          {['全部', '上架', '下架', '售罄'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-all ${
                filterStatus === s ? 'bg-rose-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-rose-300'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-rose-500 text-white px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors"
        >
          <i className="ri-add-line" />
          发布商品
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">商品</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">分类</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">价格</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">库存</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">销量</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">状态</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} className={`${i < filtered.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        {p.images?.[0] ? (
                          <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-image-line text-gray-300 text-xl" />
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-gray-800 font-medium line-clamp-2 max-w-[180px]">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500">{p.category ?? '-'}</td>
                  <td className="px-5 py-4">
                    <InlineEdit
                      value={p.price}
                      prefix="¥"
                      onSave={(v) => quickUpdate(p.id, 'price', v)}
                    />
                    {p.original_price != null && (
                      <InlineEdit
                        value={p.original_price}
                        prefix="¥"
                        onSave={(v) => quickUpdate(p.id, 'original_price', v)}
                      />
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <InlineEdit
                      value={p.stock}
                      suffix=" 件"
                      onSave={(v) => quickUpdate(p.id, 'stock', v)}
                    />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-col gap-0.5">
                      <InlineEdit
                        value={p.sales}
                        suffix=" 件"
                        onSave={(v) => quickUpdate(p.id, 'sales', v)}
                      />
                      <span className="text-[10px] text-gray-300 leading-none">点击设置虚拟销量</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusStyle[p.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setEditingProduct(p)}
                        className="text-xs text-rose-500 cursor-pointer hover:underline whitespace-nowrap"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => toggleStatus(p)}
                        disabled={updating === p.id || p.status === '售罄'}
                        className="text-xs text-gray-400 cursor-pointer hover:underline whitespace-nowrap disabled:opacity-40"
                      >
                        {updating === p.id ? '...' : p.status === '上架' ? '下架' : '上架'}
                      </button>
                      <button
                        onClick={() => setDeletingProduct(p)}
                        className="text-xs text-gray-300 cursor-pointer hover:text-rose-400 whitespace-nowrap transition-colors"
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-gray-400 text-sm">暂无商品数据</div>
        )}
      </div>

      {/* Pagination hint */}
      {!loading && filtered.length > 0 && (
        <div className="mt-4 text-xs text-gray-400 text-right">
          共 {filtered.length} 条商品
        </div>
      )}

      {showAddModal && (
        <AddProductModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchProducts();
          }}
        />
      )}

      {editingProduct && (
        <AddProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSuccess={() => {
            setEditingProduct(null);
            fetchProducts();
          }}
        />
      )}

      {/* 删除确认弹窗 */}
      {deletingProduct && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50 animate-[fadeIn_0.15s_ease]" onClick={() => !deleting && setDeletingProduct(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-2xl w-full max-w-sm p-6 animate-[slideUp_0.2s_ease]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 图标 */}
              <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-delete-bin-line text-rose-500 text-2xl" />
              </div>

              {/* 文案 */}
              <h3 className="text-base font-bold text-gray-900 text-center mb-2">确认删除商品？</h3>
              <p className="text-sm text-gray-500 text-center mb-1">
                即将删除：<span className="font-medium text-gray-700">「{deletingProduct.name}」</span>
              </p>
              <p className="text-xs text-gray-400 text-center mb-6">此操作不可恢复，相关订单数据不受影响</p>

              {/* 按钮 */}
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingProduct(null)}
                  disabled={deleting}
                  className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 bg-rose-500 text-white py-2.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors disabled:opacity-60"
                >
                  {deleting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      删除中...
                    </span>
                  ) : '确认删除'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminProductsPage;
